// w12 — Garden Cascade variant. Maximalist botanical garden wedding.
//
// "Nigora & Sherzod" — 18.06.2026 at the Aral Garden in Tashkent. The visual
// language is layered watercolour peonies, eucalyptus and tied-ribbon swirls.
//
// SIGNATURE — the floral wreath that frames the hero names is built from
// individual peony groups that GROW on load: each peony scales from 0.3 → 1.0
// with a 50ms stagger (1.5s total, ease-out), then breathes via an infinite
// 4s sway (rotate −2deg ↔ 2deg). prefers-reduced-motion freezes everything
// to 1.0 and stops the sway. On phones the sway is also disabled to save
// battery.
//
// Sound — useSound triggers:
//   * "swoosh" once when the wreath finishes blooming (hero load)
//   * "chime"  when an RSVP "yes" is confirmed
//
// Music — a small floating ambient toggle in the bottom-right plays a soft
// F-major breathing chord via the shared useAmbient hook.

import { useCallback, useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";
import type { VariantProps } from "./types";
import { useSound } from "@/lib/sound";
import { useAmbient, useReducedMotion } from "./_ambient";
import { MusicToggle } from "@/components/MusicToggle";
import {
  CountdownSection,
  StorySection,
  GallerySection,
  ScheduleSection,
  MapSection,
  RsvpSection,
  ShareSection,
  GuestFooter,
} from "@/components/sections";

// ─── Small viewport hook ────────────────────────────────────────────────────

function useIsPhone(): boolean {
  const [phone, setPhone] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth < 720;
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onResize = () => setPhone(window.innerWidth < 720);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return phone;
}

// ─── Reveal helper ──────────────────────────────────────────────────────────

interface RevealProps { children: React.ReactNode; reduce: boolean; delay?: number; style?: CSSProperties; }
function Reveal({ children, reduce, delay = 0, style }: RevealProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [shown, setShown] = useState<boolean>(reduce);
  useEffect(() => {
    if (reduce) { setShown(true); return; }
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") { setShown(true); return; }
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) if (e.isIntersecting) { setShown(true); io.disconnect(); break; }
    }, { threshold: 0.18, rootMargin: "0px 0px -8% 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, [reduce]);
  return (
    <div ref={ref} style={{
      ...style,
      opacity: shown ? 1 : 0,
      transform: shown ? "translateY(0)" : "translateY(18px)",
      transition: reduce ? "none" : `opacity 1s ease-out ${delay}ms, transform 1s ease-out ${delay}ms`,
    }}>{children}</div>
  );
}

// ─── Component ──────────────────────────────────────────────────────────────

export function GardenCascadeVariant({ t, isDark, ctx }: VariantProps) {
  const reduce = useReducedMotion();
  const phone = useIsPhone();
  const swayDisabled = reduce || phone;
  const { play, enabled: soundEnabled } = useSound();

  const [music, setMusic] = useState<boolean>(false);
  // F-major breathing chord (F3 + A3 + C4 + F4) — calm garden breath.
  useAmbient(music && soundEnabled, {
    freqs: [174.61, 220.0, 261.63, 349.23],
    type: "sine",
    master: 0.035,
    lfo: 0.1,
  });

  const heroPhoto = t.photos[0];

  // SIGNATURE: animated wreath. Each peony gets its own bloom delay; the
  // keyframe consumes it via animationDelay.
  const wreathPeonies = [
    { x: 50, y: 8, size: 1.2, hue: "#F4C8C0" },
    { x: 18, y: 14, size: 1.0, hue: "#FBF5E8" },
    { x: 82, y: 14, size: 1.0, hue: "#FBF5E8" },
    { x: 6, y: 36, size: 0.9, hue: "#E07A5F" },
    { x: 94, y: 36, size: 0.9, hue: "#E07A5F" },
    { x: 8, y: 60, size: 1.05, hue: "#F4C8C0" },
    { x: 92, y: 60, size: 1.05, hue: "#F4C8C0" },
    { x: 18, y: 82, size: 0.85, hue: "#FBF5E8" },
    { x: 82, y: 82, size: 0.85, hue: "#FBF5E8" },
    { x: 50, y: 90, size: 1.15, hue: "#E89A93" },
  ];

  // Fire swoosh once when bloom finishes (~1.5s after mount).
  useEffect(() => {
    if (reduce) { play("swoosh"); return; }
    const id = window.setTimeout(() => play("swoosh"), 1500);
    return () => window.clearTimeout(id);
  }, [play, reduce]);

  // Wrap RSVP setter so a "yes" plays a single chime.
  const wrappedRsvp = {
    ...ctx.rsvp,
    setRsvp: (v: string | null) => {
      const before = ctx.rsvp.rsvp;
      ctx.rsvp.setRsvp(v);
      if (v === "yes" && before !== "yes") play("chime");
    },
  };

  const toggleMusic = useCallback(() => {
    setMusic((p) => {
      const next = !p;
      play(next ? "music-on" : "music-off");
      return next;
    });
  }, [play]);

  const cream = t.bg;
  const ink = t.ink;
  const accent = t.accent;
  const font = t.font;

  return (
    <div className="v-w12">
      <style>{`
        @keyframes gcBloom {
          0%   { transform: scale(0.3); opacity: 0; }
          70%  { transform: scale(1.06); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes gcSway {
          0%, 100% { transform: rotate(-2deg); }
          50%      { transform: rotate(2deg); }
        }
        .gc-peony {
          animation: gcBloom 0.9s ease-out both;
          transform-origin: 50% 50%;
        }
        .gc-peony-inner {
          display: block;
          animation: gcSway 4s ease-in-out infinite;
          transform-origin: 50% 50%;
        }
        @media (prefers-reduced-motion: reduce) {
          .gc-peony { animation: none !important; transform: scale(1) !important; opacity: 1 !important; }
          .gc-peony-inner { animation: none !important; }
        }
        .gc-no-sway .gc-peony-inner { animation: none !important; }

        .gc-music-btn {
          transition: transform .2s, background .2s, box-shadow .25s;
        }
        .gc-music-btn:hover { transform: scale(1.08); box-shadow: 0 12px 32px ${ink}40; }

        .gc-sched-row {
          transition: background .25s ease-out, transform .25s ease-out;
        }
        .gc-sched-row:hover { background: ${accent}14; transform: translateX(4px); }

        .gc-rsvp-btn, button[aria-pressed] {
          transition: transform .2s ease-out, box-shadow .25s ease-out, background .25s ease-out;
        }
        .gc-rsvp-btn:hover, button[aria-pressed]:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 24px ${ink}26;
        }

        @media (max-width: 720px) {
          .gc-hero-names { font-size: clamp(46px, 13vw, 88px) !important; }
        }
        @media (max-width: 768px) {
          .v-w12 .v-w12-hero { padding: 64px 18px 48px !important; min-height: auto !important; }
          .v-w12 .gc-hero-names { font-size: clamp(36px, 12vw, 72px) !important; }
          .v-w12 .gc-music-btn { min-height: 44px; min-width: 44px; }
          /* Hero nameplate: switch from absolute-fill to static flow on mobile
             so the names are never hidden behind the wreath layers */
          .v-w12 .v-w12-hero { min-height: min(100vh, 520px) !important; }
          /* Date/venue inline: trim letter-spacing and allow wrap */
          .v-w12 .gc-date-venue {
            font-size: 14px !important;
            letter-spacing: 0.08em !important;
            white-space: normal !important;
            word-break: break-word !important;
          }
          /* Wreath decorative layer: reduce so it doesn't overflow narrow viewport */
          .v-w12 .gc-wreath-layer { width: min(320px, 90vw) !important; }
        }
        .v-w12 button:focus-visible { outline: 2px solid ${accent}; outline-offset: 2px; }
      `}</style>

      {/* ── HERO — full-bleed photo + giant floral wreath ─────────────── */}
      <section className="v-w12-hero" style={{
        position: "relative", minHeight: "100vh",
        background: cream, color: ink, overflow: "hidden",
      }}>
        {/* photo bleed */}
        <div style={{
          position: "absolute", inset: 0,
          backgroundImage: heroPhoto
            ? `linear-gradient(180deg, rgba(245,240,226,0.25), rgba(245,240,226,0.55)), url("${heroPhoto}")`
            : `linear-gradient(180deg, ${cream}, #E9DFC4)`,
          backgroundSize: "cover", backgroundPosition: "center",
        }} />

        {/* giant wreath — wraps top + sides of names */}
        <div className={`gc-wreath-layer${swayDisabled ? " gc-no-sway" : ""}`}
          style={{
            position: "absolute",
            left: "50%", top: "50%", transform: "translate(-50%, -50%)",
            width: "min(880px, 92vw)", aspectRatio: "1.05 / 1",
            pointerEvents: "none",
          }}>
          {/* eucalyptus arc — base ring of foliage */}
          <svg viewBox="0 0 100 100" preserveAspectRatio="none"
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}>
            <ellipse cx="50" cy="50" rx="46" ry="42"
              fill="none" stroke="#7A8C56" strokeWidth="0.3" opacity="0.0" />
            {/* wreath leaves around the perimeter (top + sides only — bottom is open for date line) */}
            {Array.from({ length: 36 }).map((_, i) => {
              const a = (i / 36) * Math.PI * 2 - Math.PI / 2;
              // skip the bottom-third arc to keep wreath as a top+side frame
              const deg = (a * 180) / Math.PI;
              if (deg > 30 && deg < 150) return null;
              const cx = 50 + Math.cos(a) * 46;
              const cy = 50 + Math.sin(a) * 42;
              const rot = (deg + 90) % 360;
              const tone = i % 2 === 0 ? "#7A8C56" : "#8FA06B";
              return (
                <g key={i} transform={`translate(${cx} ${cy}) rotate(${rot})`}>
                  <ellipse cx="0" cy="-1.4" rx="2.2" ry="1.0" fill={tone} opacity="0.85" />
                  <ellipse cx="0" cy="1.4" rx="2.2" ry="1.0" fill={tone} opacity="0.7" />
                </g>
              );
            })}
            {/* berry dots */}
            {[20, 30, 70, 80].map((p) => {
              const a = (p / 100) * Math.PI * 2 - Math.PI / 2;
              return <circle key={p} cx={50 + Math.cos(a) * 46} cy={50 + Math.sin(a) * 42} r="1" fill="#6B1F2A" />;
            })}
          </svg>

          {/* peonies in the wreath — each blooms with stagger */}
          {wreathPeonies.map((p, i) => {
            const delay = i * 50; // ms
            const peonyStyle: CSSProperties = reduce
              ? { transform: "scale(1)", opacity: 1 }
              : { animationDelay: `${delay}ms` };
            const swayStyle: CSSProperties = reduce
              ? {}
              : { animationDelay: `${delay}ms` };
            const sz = 80 * p.size; // px
            return (
              <div key={i} style={{
                position: "absolute",
                left: `${p.x}%`, top: `${p.y}%`,
                width: sz, height: sz,
                marginLeft: -sz / 2, marginTop: -sz / 2,
              }}>
                <div className="gc-peony" style={peonyStyle}>
                  <div className="gc-peony-inner" style={swayStyle}>
                    <svg viewBox="-30 -30 60 60" style={{ width: "100%", height: "100%", display: "block" }}>
                      {[0, 45, 90, 135, 180, 225, 270, 315].map((a) => (
                        <ellipse key={`o-${a}`} cx="0" cy="-14" rx="11" ry="16" fill={p.hue} opacity="0.78"
                          transform={`rotate(${a})`} />
                      ))}
                      {[22, 67, 112, 157, 202, 247, 292, 337].map((a) => (
                        <ellipse key={`i-${a}`} cx="0" cy="-9" rx="8" ry="12" fill="#FBF5E8" opacity="0.92"
                          transform={`rotate(${a})`} />
                      ))}
                      <circle cx="0" cy="0" r="5" fill="#C9844F" opacity="0.85" />
                      <circle cx="-1.5" cy="-1.5" r="1.6" fill="#5C3210" opacity="0.6" />
                    </svg>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* name plate sits inside the wreath */}
        <div style={{
          position: "absolute", inset: 0,
          display: "grid", placeItems: "center",
          textAlign: "center", padding: "0 24px",
          zIndex: 3,
        }}>
          <div>
            <div style={{
              fontFamily: font, fontStyle: "italic",
              fontSize: 14, color: accent, letterSpacing: "0.32em",
              marginBottom: 18,
            }}>
              ~ AR AL  G AR D EN ~
            </div>
            <h1 className="gc-hero-names" style={{
              fontFamily: font, fontStyle: "italic", fontWeight: 400,
              fontSize: "clamp(56px, 9.2vw, 132px)", lineHeight: 1, color: ink,
              margin: 0,
              textShadow: `0 1px 0 ${cream}`,
            }}>
              {t.names}
            </h1>
            <div className="gc-date-venue" style={{
              marginTop: 24, fontFamily: font, fontStyle: "italic",
              fontSize: 18, color: ink, letterSpacing: "0.18em",
            }}>
              {t.date} · {t.venue}
            </div>
          </div>
        </div>
      </section>

      {/* ── COUNTDOWN ─────────────────────────────────────────────────── */}
      <Reveal reduce={reduce}>
        <CountdownSection t={t} {...ctx.countdown} isDark={isDark} />
      </Reveal>

      {/* ── STORY ─────────────────────────────────────────────────────── */}
      <Reveal reduce={reduce}>
        <StorySection
          t={t}
          eyebrow="Bizning bog'imiz"
          title="Aral bog'idagi bir tup atirgul"
          body={
            "Nigora va Sherzod Aral bog'ida tanishishgan: bir yil davomida bitta atirgul tubini birga parvarish qilishdi. " +
            "Atirgul gullaganda Sherzod taklif qildi — endi ular sizni o'sha bog'ga, hayoti boshlangan joyga taklif qilishadi."
          }
        />
      </Reveal>

      {/* ── GALLERY ──────────────────────────────────────────────────── */}
      <Reveal reduce={reduce}><GallerySection t={t} /></Reveal>

      {/* ── SCHEDULE ─────────────────────────────────────────────────── */}
      <Reveal reduce={reduce}>
        <div className="gc-schedule-wrap">
          <ScheduleSection
            t={t}
            items={[
              ["16:00", "Mehmonlarni gulli kirish darvozasida kutib olamiz"],
              ["17:00", "Nikoh marosimi — pion arkasi ostida"],
              ["17:30", "Fotosurat bog' ichida — lavanda va atirgul orasida"],
              ["18:30", "Osh va kechki dastur — gulzor osh joyida"],
              ["20:00", "Raqs kechasi — gulzor o'rtasida, jonli musiqa bilan"],
              ["22:30", "Yakuniy duo va mehmonlarni kuzatish"],
            ]}
          />
        </div>
      </Reveal>

      {/* ── MAP ──────────────────────────────────────────────────────── */}
      <Reveal reduce={reduce}>
        <MapSection t={t} isDark={isDark} addressLine="Aral Bog'i, Toshkent shahri, Botanika ko'chasi" />
      </Reveal>

      {/* ── RSVP ─────────────────────────────────────────────────────── */}
      <Reveal reduce={reduce}>
        <RsvpSection
          t={t}
          isDark={isDark}
          {...wrappedRsvp}
          slug={ctx.slug}
          label="Gulzorimizda sizni kutamiz"
        />
      </Reveal>

      {/* ── SHARE ────────────────────────────────────────────────────── */}
      <ShareSection t={t} />

      {/* ── FOOTER ───────────────────────────────────────────────────── */}
      <GuestFooter t={t} />


      {/* ── Floating music toggle ───────────────────────────────────── */}
      <MusicToggle
        on={music}
        onToggle={toggleMusic}
        accent={accent}
        ink={cream}
        style={{ bottom: 20, right: 20 }}
        className="gc-music-btn"
        label={{ play: "Bog' musiqasini yoqish", pause: "Bog' musiqasini o'chirish" }}
      />
    </div>
  );
}
